import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { BrandingSettings } from '../types/branding'
import { cacheConfig } from '../lib/react-query'
import { useNotifications } from './useNotifications'
import { useOrganization } from '../contexts/OrganizationContext'

export function useBranding() {
  const queryClient = useQueryClient()
  const { showNotification } = useNotifications()
  const { organization } = useOrganization()

  const {
    data: branding,
    isLoading,
    error,
    refetch: refreshBranding
  } = useQuery({
    queryKey: ['branding', organization?.id],
    queryFn: async (): Promise<BrandingSettings | null> => {
      if (!organization?.id) return null
      
      const { data, error } = await supabase
        .from('branding_settings')
        .select('*')
        .eq('organization_id', organization.id)
        .limit(1)

      if (error) {
        console.error('Error fetching branding settings:', error)
        return null
      }

      // Return first row if exists, otherwise null
      return data && data.length > 0 ? data[0] : null
    },
    enabled: !!organization?.id,
    ...cacheConfig.semiStatic
  })

  const updateBrandingMutation = useMutation({
    mutationFn: async (updates: Partial<BrandingSettings>) => {
      if (!organization?.id) {
        throw new Error('No organization context available')
      }

      if (!branding?.id) {
        // Create new branding settings with organization_id
        const { data, error } = await supabase
          .from('branding_settings')
          .insert({
            ...updates,
            organization_id: organization.id
          })
          .select()
          .single()

        if (error) throw error
        return data
      } else {
        // Update existing branding settings
        const { data, error } = await supabase
          .from('branding_settings')
          .update(updates)
          .eq('id', branding.id)
          .eq('organization_id', organization.id)
          .select()
          .single()

        if (error) throw error
        return data
      }
    },
    onSuccess: (updatedBranding) => {
      queryClient.setQueryData(['branding', organization?.id], updatedBranding)
      showNotification({
        type: 'success',
        title: 'Branding Updated',
        message: 'Your branding settings have been saved successfully.'
      })
    },
    onError: (error) => {
      console.error('Error updating branding:', error)
      showNotification({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update branding settings. Please try again.'
      })
    }
  })

  const updateBranding = async (updates: Partial<BrandingSettings>) => {
    await updateBrandingMutation.mutateAsync(updates)
  }

  return {
    branding,
    isLoading,
    error,
    updateBranding,
    refreshBranding,
    isUpdating: updateBrandingMutation.isPending
  }
}

// Hook for getting theme colors based on branding
// NOTE: Prefer importing useThemeColors from hooks/useThemeColors.ts for full design system support.
// This version only provides basic branding colors.
export function useBrandingColors() {
  const { branding } = useBranding()

  return {
    primary: branding?.primary_color || '#00f2ff',
    secondary: branding?.secondary_color || '#06B6D4',
    accent: branding?.accent_color || '#22D3EE',
    background: {
      primary: branding?.theme_mode === 'light' ? '#ffffff' : '#1a1a1a',
      secondary: branding?.theme_mode === 'light' ? '#f8f9fa' : '#2d2d2d'
    },
    text: {
      primary: branding?.theme_mode === 'light' ? '#1a1a1a' : '#ffffff',
      secondary: branding?.theme_mode === 'light' ? '#6b7280' : '#9ca3af'
    },
    border: branding?.theme_mode === 'light' ? '#e5e7eb' : '#374151'
  }
}

// Hook for company information
export function useCompanyInfo() {
  const { branding } = useBranding()

  return {
    companyName: branding?.company_name || 'The Hockey Factory',
    contactEmail: branding?.contact_email,
    contactPhone: branding?.contact_phone,
    address: {
      line1: branding?.address_line1,
      line2: branding?.address_line2,
      city: branding?.city,
      state: branding?.state,
      zipCode: branding?.zip_code,
      country: branding?.country || 'United States'
    },
    website: branding?.website_url,
    social: {
      facebook: branding?.social_facebook,
      instagram: branding?.social_instagram,
      twitter: branding?.social_twitter
    },
    logoUrl: branding?.logo_url,
    faviconUrl: branding?.favicon_url
  }
}